import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, CaretRight, Phone, FilePdf, FileXls, CalendarBlank, Funnel, Warning, UploadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { MONTHS } from "./comercialConstants";
import { DuplicatesPanel } from "./DuplicatesPanel";
import { ComercialStatusBadge } from "./ComercialStatusBadge";
import { ComercialStatusManager } from "./ComercialStatusManager";
import { ComercialForm } from "./ComercialForm";
import { ComercialDetail } from "./ComercialDetail";
import { QuickInteraction, QuickFollowUp, QuickQuotations, QuickTickets } from "./ComercialQuickActions";

const ComercialModule = ({ onCreateQuotation, onViewQuotation }) => {
  const [contacts, setContacts] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [filterSeller, setFilterSeller] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [importing, setImporting] = useState(false);
  const { token, user } = useAuth();

  const fetchStatusTypes = async () => {
    try {
      const res = await axios.get(`${API}/comercial-status-types`);
      setStatusTypes(res.data);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  };

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (filterMonth !== "all") {
        params.append("month", filterMonth);
        params.append("year", filterYear);
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/comercial${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      setContacts(res.data);
    } catch (e) { toast.error("Error al cargar contactos"); }
  };

  const hasActiveDateFilters = dateFrom || dateTo || filterMonth !== "all";

  useEffect(() => { fetchStatusTypes(); }, []);
  useEffect(() => { fetchContacts(); }, [token, dateFrom, dateTo, filterMonth, filterYear]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterStatus, filterSeller]);

  if (selectedId) {
    return <ComercialDetail contactId={selectedId} statusTypes={statusTypes} onBack={() => { setSelectedId(null); fetchContacts(); }} onCreateQuotation={onCreateQuotation} onViewQuotation={onViewQuotation} />;
  }

  if (showNew) {
    return <ComercialForm statusTypes={statusTypes} onSuccess={() => { setShowNew(false); fetchContacts(); }} onCancel={() => setShowNew(false)} />;
  }

  const filtered = contacts.filter(c => {
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    const matchesSearch = !searchTerm ||
      (c.company_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.contact_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.city || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeller = filterSeller === "all" ||
      (filterSeller === "unassigned" ? !c.assigned_to_id : c.assigned_to_id === filterSeller);
    return matchesStatus && matchesSearch && matchesSeller;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginatedContacts = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const startIdx = filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, filtered.length);

  const activeFilterCount = [filterStatus !== "all", filterSeller !== "all", hasActiveDateFilters, searchTerm.trim()].filter(Boolean).length;

  const statusCounts = {};
  contacts.forEach(c => { statusCounts[c.status] = (statusCounts[c.status] || 0) + 1; });

  const sellerOptions = [];
  const seenSellers = new Set();
  contacts.forEach(c => {
    if (c.assigned_to_id && !seenSellers.has(c.assigned_to_id)) {
      seenSellers.add(c.assigned_to_id);
      sellerOptions.push({ id: c.assigned_to_id, name: c.assigned_to_name || "Sin nombre" });
    }
  });
  const unassignedCount = contacts.filter(c => !c.assigned_to_id).length;

  const clearDateFilters = () => { setDateFrom(""); setDateTo(""); setFilterMonth("all"); setFilterYear(String(new Date().getFullYear())); };
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/comercial/export/excel`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `comercial_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) { toast.error("Error al exportar Excel"); }
  };

  const handleExportPDF = async () => {
    try {
      const res = await axios.get(`${API}/comercial/export/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `reporte_comercial_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) { toast.error("Error al exportar PDF"); }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/comercial/import/excel`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const d = res.data;
      toast.success(`Importacion completa: ${d.imported} nuevos, ${d.updated} actualizados, ${d.skipped} saltados`);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
    e.target.value = "";
  };

  return (
    <div className={`grid grid-cols-1 ${user?.role === 'admin' ? 'lg:grid-cols-[1fr_280px]' : ''} gap-5`} data-testid="comercial-module">
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-5">
        <h1 className="text-2xl font-bold text-slate-900">Comercial <span className="text-base font-normal text-slate-400 ml-2">({filtered.length})</span></h1>
        <div className="flex flex-wrap gap-2">
          {user?.role === 'admin' && (
            <>
              <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={handleExportExcel} data-testid="export-comercial-excel">
                <FileXls size={16} className="mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" className="text-red-700 border-red-200 hover:bg-red-50" onClick={handleExportPDF} data-testid="export-comercial-pdf">
                <FilePdf size={16} className="mr-1" /> PDF
              </Button>
              <label className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border rounded-md transition-colors ${importing ? "opacity-50 cursor-wait text-slate-400 border-slate-200" : "cursor-pointer text-blue-700 border-blue-200 hover:bg-blue-50"}`} data-testid="import-comercial-excel">
                <UploadSimple size={16} /> {importing ? "Importando..." : "Importar"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportExcel} disabled={importing} />
              </label>
            </>
          )}
          <Button className="btn-primary" size="sm" onClick={() => setShowNew(true)} data-testid="new-comercial-btn">
            <Plus size={16} className="mr-1" /> Nuevo Contacto
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 p-4 mb-4 space-y-3" data-testid="comercial-filter-bar">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-sharp pl-10"
              placeholder="Empresa / Contacto / Ciudad / Email / Celular..."
              data-testid="comercial-search"
            />
          </div>
          <Select value={filterSeller} onValueChange={setFilterSeller}>
            <SelectTrigger className="input-sharp w-full md:w-56" data-testid="comercial-filter-seller">
              <SelectValue placeholder="Todos los vendedores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los vendedores</SelectItem>
              <SelectItem value="unassigned">Sin asignar ({unassignedCount})</SelectItem>
              {sellerOptions.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className={`${hasActiveDateFilters ? 'border-blue-400 text-blue-700 bg-blue-50' : ''}`} onClick={() => setShowDateFilters(!showDateFilters)} data-testid="toggle-comercial-date-filters">
            <CalendarBlank size={16} className="mr-1" /> Fechas
          </Button>
          {user?.role === 'admin' && (
            <Button variant="outline" size="sm" onClick={() => setShowStatusManager(!showStatusManager)} data-testid="toggle-comercial-status-manager">
              <Funnel size={16} className="mr-1" /> Estatus
            </Button>
          )}
        </div>

        {/* Date Filters */}
        {showDateFilters && (
          <div className="border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="comercial-date-filters">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Desde</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-sharp" data-testid="comercial-date-from" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Hasta</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-sharp" data-testid="comercial-date-to" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Mes</label>
                <Select value={filterMonth} onValueChange={setFilterMonth}>
                  <SelectTrigger className="input-sharp" data-testid="comercial-filter-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Ano</label>
                <Select value={filterYear} onValueChange={setFilterYear}>
                  <SelectTrigger className="input-sharp" data-testid="comercial-filter-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {hasActiveDateFilters && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" className="text-xs text-red-500" onClick={clearDateFilters}>Limpiar filtros de fecha</Button>
              </div>
            )}
          </div>
        )}

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus("all")}
            className={`px-3 py-1.5 text-xs font-medium border rounded-full transition-colors ${filterStatus === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
            data-testid="comercial-filter-all"
          >
            Todos ({contacts.length})
          </button>
          {statusTypes.map(st => (
            <button
              key={st.name}
              onClick={() => setFilterStatus(filterStatus === st.name ? "all" : st.name)}
              className={`px-3 py-1.5 text-xs font-medium border rounded-full transition-colors ${filterStatus === st.name ? `${st.color} font-semibold` : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              data-testid={`comercial-filter-${st.name}`}
            >
              {st.label} ({statusCounts[st.name] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* Status Manager */}
      {showStatusManager && user?.role === 'admin' && (
        <ComercialStatusManager statusTypes={statusTypes} onRefresh={fetchStatusTypes} token={token} />
      )}

      {/* Active Filters Summary */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 mb-3 text-xs text-slate-500" data-testid="comercial-active-filters">
          <Funnel size={14} />
          <span>{activeFilterCount} filtro(s) activo(s)</span>
          <span className="text-slate-300">|</span>
          <span>{filtered.length} resultado(s)</span>
          <button onClick={() => { setFilterStatus("all"); setFilterSeller("all"); setSearchTerm(""); clearDateFilters(); }} className="text-red-500 hover:underline ml-2">Limpiar todo</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-hidden" data-testid="comercial-table">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Estatus</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Empresa / Contacto</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vendedor</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Contacto</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ubicacion</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedContacts.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-slate-400">
                  <MagnifyingGlass size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No se encontraron contactos</p>
                </td>
              </tr>
            ) : (
              paginatedContacts.map(c => (
                <tr key={c.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedId(c.id)} data-testid={`comercial-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <ComercialStatusBadge status={c.status} statusTypes={statusTypes} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 text-sm">{c.company_name}</div>
                    <div className="text-xs text-slate-500">{c.contact_name}</div>
                    {c.email && <div className="text-xs text-slate-400">{c.email}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {c.assigned_to_name ? (
                      <span className="text-sm text-blue-700 font-medium">{c.assigned_to_name}</span>
                    ) : (
                      <span className="text-xs text-slate-400">{"\u2014"}</span>
                    )}
                    {c.created_by_name && <div className="text-xs text-slate-400">Creado: {c.created_by_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {c.phone ? (
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Phone size={14} className="text-slate-400" /> {c.phone}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">{"\u2014"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.city ? (
                      <span className="text-sm text-slate-600">{c.city}{c.state ? `, ${c.state}` : ""}</span>
                    ) : (
                      <span className="text-xs text-slate-400">{"\u2014"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      <QuickInteraction contactId={c.id} token={token} />
                      <QuickFollowUp contactId={c.id} token={token} />
                      <QuickQuotations contactId={c.id} token={token} onViewQuotation={onViewQuotation} />
                      <QuickTickets contactId={c.id} companyName={c.company_name} token={token} />
                      <button onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }} className="w-7 h-7 rounded flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Ver detalle" data-testid={`view-detail-${c.id}`}>
                        <CaretRight size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-3" data-testid="comercial-pagination">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>Mostrando {startIdx}-{endIdx} de {filtered.length}</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="input-sharp w-24" data-testid="comercial-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="15">15</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400">por pagina</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="comercial-prev-page">Anterior</Button>
          <div className="flex gap-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) { pageNum = i + 1; }
              else if (currentPage <= 3) { pageNum = i + 1; }
              else if (currentPage >= totalPages - 2) { pageNum = totalPages - 4 + i; }
              else { pageNum = currentPage - 2 + i; }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-sm rounded ${currentPage === pageNum ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  data-testid={`comercial-page-${pageNum}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="comercial-next-page">Siguiente</Button>
        </div>
      </div>
    </div>

    {/* RIGHT: Duplicates Panel (Admin only) */}
    {user?.role === 'admin' && (
    <div className="hidden lg:block">
      <div className="bg-white border rounded-xl overflow-hidden sticky top-4">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <Warning size={14} className="text-amber-600" />
          <span className="text-xs font-bold text-slate-800">Avisos y Duplicados</span>
        </div>
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-3">
          <DuplicatesPanel />
        </div>
      </div>
    </div>
    )}
    </div>
  );
};

export { ComercialModule };
